import React from 'react';
import { X, Sparkles } from 'lucide-react';
import { usePlan } from '../contexts/PlanContext';

// Early-access framing: usage is capped while we onboard our first cohort.
// No paid tiers are surfaced — limits expand as we open access.
const FEATURE_MESSAGES: Record<string, { title: string; description: string }> = {
  projects: {
    title: 'You\'ve filled your project slots',
    description: 'Early-access workspaces start with a focused set of projects so the AI stays sharp on each one.',
  },
  members: {
    title: 'This project\'s team is full',
    description: 'Early-access projects start with a focused team. More seats open up as we expand access.',
  },
  ai_credits: {
    title: 'You\'re out of AI runs for now',
    description: 'Your AI allowance resets each month. You clearly put it to work — that\'s exactly what we want to see.',
  },
  storage: {
    title: 'Storage is full',
    description: 'Connect Google Drive to keep going, or clear space. More storage opens up as we expand access.',
  },
  reports: {
    title: 'Deeper analytics are coming',
    description: 'Executive dashboards and full reporting are rolling out to early-access workspaces.',
  },
  ai_export: {
    title: 'Full AI export is coming',
    description: 'Complete CLAUDE.md and .cursorrules export is rolling out to early-access workspaces.',
  },
  default: {
    title: 'This is opening up soon',
    description: 'This capability is rolling out to early-access workspaces.',
  },
};

const UpgradePrompt: React.FC = () => {
  const { showUpgrade, upgradeFeature, closeUpgradePrompt } = usePlan();

  if (!showUpgrade) return null;

  const msg = FEATURE_MESSAGES[upgradeFeature] || FEATURE_MESSAGES.default;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={closeUpgradePrompt} />
      <div className="relative bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4">
          <button
            onClick={closeUpgradePrompt}
            className="absolute top-4 right-4 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-secondary-100 dark:bg-secondary-700 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-secondary-900 dark:text-white">{msg.title}</h3>
            </div>
          </div>
          <p className="text-sm text-secondary-500 dark:text-secondary-400 leading-relaxed">{msg.description}</p>
        </div>

        {/* Reassurance */}
        <div className="px-6 pb-2">
          <p className="text-sm text-secondary-600 dark:text-secondary-300 bg-secondary-50 dark:bg-secondary-900/40 border border-secondary-200 dark:border-secondary-700 rounded-xl px-4 py-3">
            You&apos;re one of our first builders. We&apos;ll open up more headroom as early access expands — and you&apos;ll hear from us first.
          </p>
        </div>

        {/* Action */}
        <div className="px-6 py-6">
          <button
            onClick={closeUpgradePrompt}
            className="w-full py-2.5 px-4 bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 rounded-xl text-sm font-semibold hover:bg-black dark:hover:bg-secondary-100 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
